import React, { useState, useRef, useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import AdminLayout from '../admin/AdminDashboardLayout';
import { useApiQuery } from '../../hooks/useApiQuery';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { useDashboardRole } from '../../hooks/useDashboardRole';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

type Canvasser = {
	actorId: number;
	fullName: string;
	active: boolean;
	latitude: number | null;
	longitude: number | null;
	accuracyMeters: number | null;
	locationSource: string | null;
	locationLabel: string | null;
	recordedAt: string | null;
};

type ActivityActor = {
	id: number;
	email: string;
	fullName: string;
	role: string;
	enabled: boolean;
	createdAt: string;
	updatedAt: string;
	lastLoginIp?: string;
	lastLoginLocation?: string;
	lastLoginAt?: string;
	phone?: string | null;
	mode: string;
};

type ActivityLog = {
	id: number;
	partner: {
		id: number;
		name: string;
		orgType: string;
		partnerIdentifier: string;
		legalName: string;
		contact: string;
		website: string;
		createdAt: string;
		updatedAt: string;
		enabled: boolean;
		status: string;
		enabledModules: string[];
	};
	actor: ActivityActor;
	type: string;
	message: string;
	createdAt: string;
};

type ActivityLogsResponse = {
	success: boolean;
	message: string;
	data: {
		content: ActivityLog[];
		pageNumber: number;
		pageSize: number;
		totalElements: number;
		totalPages: number;
		first: boolean;
		last: boolean;
	};
};

type FieldOperationsResponse = {
	success: boolean;
	message: string;
	data: {
		totalRegistrations: number;
		activeCanvassers: number;
		inactiveCanvassers: number;
		compliancePercent: number;
		canvassers: Canvasser[];
	};
};

const FieldActivity = () => {
	const { dashboardRole } = useDashboardRole();
	const [sheetHeight, setSheetHeight] = useState(50); // Percentage of viewport height
	const [selectedCategory, setSelectedCategory] = useState<string>('ALL');
	const containerRef = useRef<HTMLDivElement>(null);
	const mapContainerRef = useRef<HTMLDivElement>(null);
	const mapRef = useRef<mapboxgl.Map | null>(null);
	const [selectedCanvasser, setSelectedCanvasser] = useState<Canvasser | null>(null);

	// Constants for sheet heights
	const collapsedHeight = 20;
	const expandedHeight = 80;

	// Toggle between collapsed and expanded states
	const toggleSheet = () => {
		setSheetHeight(sheetHeight === collapsedHeight ? expandedHeight : collapsedHeight);
	};

	// API query for field operations data
	const { data: fieldData, isLoading } = useApiQuery<FieldOperationsResponse>({
		queryKey: ['field-operations'],
		request: {
			method: 'GET',
			url: '/analytics/partner/field-operations',
		},
	});

	// API query for activity logs
	const { data: activityData, isLoading: isActivityLoading } = useApiQuery<ActivityLogsResponse>({
		queryKey: ['activity-logs', selectedCategory],
		request: {
			method: 'GET',
			url: selectedCategory === 'ALL' 
				? '/activity-logs'
				: `/activity-logs?activity=${selectedCategory}`,
		},
	});

	const getInitials = (fullName: string): string => {
		return fullName
			?.split(' ')
			?.map(word => word.charAt(0).toUpperCase())
			?.slice(0, 2)
			?.join('') || '';
	};

	const getAvatarColor = (role: string): string => {
		const colors: { [key: string]: string } = {
			'CANVASSER': 'bg-blue-500',
			'PARTNER_ADMIN': 'bg-purple-500',
			'SYSTEM_ADMIN': 'bg-red-500',
		};
		return colors[role] || 'bg-gray-500';
	};

	// Helper function to get status indicator color
	const getStatusColor = (type: string): string => {
		const colors: { [key: string]: string } = {
			'SYSTEM_EVENT': 'bg-green-500',
			'CANVASSING': 'bg-blue-500',
			'ENGAGEMENTS': 'bg-emerald-500',
			'ALERTS': 'bg-orange-500',
		};
		return colors[type] || 'bg-gray-500';
	};

	useEffect(() => {
		if (!mapContainerRef.current) return;

		mapRef.current = new mapboxgl.Map({
			container: mapContainerRef.current,
			style: 'mapbox://styles/mapbox/light-v11',
			center: [-95.7129, 37.0902], // Center of USA
			zoom: 4,
			attributionControl: false,
		});

		mapRef.current.addControl(new mapboxgl.AttributionControl(), 'bottom-right');

		return () => {
			mapRef.current?.remove();
		};
	}, []);

	// Add markers for canvassers when data is available
	useEffect(() => {
		if (!mapRef.current || !fieldData?.data.canvassers) return;

		const map = mapRef.current;
		const canvassers = fieldData.data.canvassers;

		// Remove existing markers
		const existingMarkers = document.querySelectorAll('.canvasser-marker');
		existingMarkers.forEach(marker => marker.remove());

		// Add new markers for canvassers with valid coordinates
		canvassers.forEach(canvasser => {
			if (canvasser.latitude && canvasser.longitude) {
				// Create marker element
				const markerEl = document.createElement('div');
				markerEl.className = 'canvasser-marker';
				markerEl.style.cursor = 'pointer';

				// Set marker color based on active status
				const markerColor = canvasser.active ? '#13ec5b' : '#ef4444'; // green for active, red for inactive

				markerEl.innerHTML = `
          <div style="
            background-color: ${markerColor};
            width: 20px;
            height: 20px;
            border-radius: 50%;
            border: 3px solid white;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
            ${canvasser.active ? 'animation: pulse 2s infinite;' : ''}
          "></div>
        `;

				// Create popup
				const popup = new mapboxgl.Popup({
					offset: 25,
					closeButton: true,
					closeOnClick: false
				}).setHTML(`
          <div style="padding: 12px; border-radius: 40px; min-width: 200px; color: white">
            <h3 style="margin: 0 0 8px 0; font-weight: bold; color: #1f2937;">${canvasser.fullName}</h3>
            <div style="font-size: 12px; margin-bottom: 4px;">
              <span style="font-weight: bold; color: black">Status:</span> 
              <span style="color: ${canvasser.active ? '#13ec5b' : '#ef4444'}; font-weight: bold;">
                ${canvasser.active ? 'Active' : 'Inactive'}
              </span>
            </div>

						 ${canvasser.recordedAt && `
              <div style="font-size: 12px; color: #9ca3af;">
                <span style="font-weight: bold;">Active since:</span>  ${formatDistanceToNow(new Date(canvasser.recordedAt), { addSuffix: true })}
              </div>
            `}
            ${canvasser.locationLabel && `
              <div style="font-size: 12px; margin-bottom: 4px; color: #1f2937;">
                <span style="font-weight: bold; color: #1f2937;">Location:</span> ${canvasser.locationLabel}
              </div>
            ` }
            ${canvasser.locationSource && `
              <div style="font-size: 12px; margin-bottom: 4px; color: #1f2937;">
                <span style="font-weight: bold; color: #1f2937;">Source:</span> ${canvasser.locationSource}
              </div>
            `}
            ${canvasser.accuracyMeters && `
              <div style="font-size: 12px; margin-bottom: 4px; color: #1f2937;">
                <span style="font-weight: bold; color: #1f2937;">Accuracy:</span> ±${canvasser.accuracyMeters}m
              </div>
            `}
           
          </div>
        `);

				// Create and add marker with popup
				const marker = new mapboxgl.Marker({
					element: markerEl,
					anchor: 'center'
				})
					.setLngLat([canvasser.longitude, canvasser.latitude])
					.setPopup(popup)
					.addTo(map);

				// Handle marker click
				markerEl.addEventListener('click', () => {
					setSelectedCanvasser(canvasser);
				});
			}
		});

		// Add pulse animation
		const style = document.createElement('style');
		style.textContent = `
      @keyframes pulse {
        0% { transform: scale(1); opacity: 1; }
        50% { transform: scale(1.2); opacity: 0.7; }
        100% { transform: scale(1); opacity: 1; }
      }
    `;
		document.head.appendChild(style);

	}, [fieldData]);

	return (
		<AdminLayout role={dashboardRole}>
			<div>

				<h1 className="text-xl text-slate-900 mb-3 font-bold border-l-2 border-slate-800 pl-2 bg-slate-200 p-1.5">Field Operations</h1>
				<div className="rounded-2xl relative bg-[#f6f8f6] dark:bg-slate-900 text-slate-900 dark:text-white font-display  min-h-screen h-full w-full flex flex-col">

					{/* <!-- Main Content Area (Map Background) --> */}
					<main className="relative rounded-2xl flex-1 w-full h-full z-0">
						{/* <!-- Map Visualization --> */}
						<div className="absolute inset-0 w-full h-full">
							{/* Loading overlay */}
							{isLoading && (
								<div className="absolute inset-0 z-10 bg-gray-900/80 flex items-center justify-center">
									<div className="text-center">
										<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-3"></div>
										<p className="text-white">Loading field operations...</p>
									</div>
								</div>
							)}

							{/* Map container */}
							<div ref={mapContainerRef} className="w-full rounded-2xl h-full" />

							{/* Map Overlay Gradient for better text readability at top */}
							<div className="absolute top-0 left-0 w-full h-48  rounded-xl  bg-gradient-to-b from-slate-400 to-transparent opacity-90 pointer-events-none"></div>
						</div>
						{/* <!-- Floating Stats Cards (Top) --> */}
						<div className="absolute top-5 left-0 w-full z-10 px-4">
							<div className="flex gap-3 overflow-x-auto no-scrollbar pb-2 snap-x">
								{/* <!-- Stat Card 1 --> */}
								<div className="snap-start shrink-0 min-w-[140px] p-3 rounded-2xl bg-[#1c2b21]/90 backdrop-blur-md border border-white/5 shadow-lg flex flex-col gap-1">
									<div className="flex justify-between items-start">
										<span className="text-xs text-gray-400 font-medium uppercase tracking-wider">Total Registrations</span>
										{/* <span className="material-symbols-outlined text-[#13ec5b] text-base">door_front</span> */}
									</div>
									<div className="flex items-end gap-2 mt-1">
										<span className="text-2xl font-bold text-white leading-none">
											{fieldData?.data.totalRegistrations?.toLocaleString() || '0'}
										</span>
									</div>
								</div>
								{/* <!-- Stat Card 2 --> */}
								<div className="snap-start shrink-0 min-w-[140px] p-3 rounded-2xl bg-[#1c2b21]/90 backdrop-blur-md border border-white/5 shadow-lg flex flex-col gap-1">
									<div className="flex justify-between items-start">
										<span className="text-xs text-gray-400 font-medium uppercase tracking-wider">Active Canvassers</span>
									</div>
									<div className="flex items-end gap-2 mt-1">
										<span className="text-2xl font-bold text-white leading-none">
											{fieldData?.data.activeCanvassers || '0'}
										</span>
										<span className="text-[10px] text-gray-400 font-bold mb-0.5">
											/ {(fieldData?.data.activeCanvassers || 0) + (fieldData?.data.inactiveCanvassers || 0)}
										</span>
									</div>
								</div>
								{/* <!-- Stat Card 3 --> */}
								<div className="snap-start shrink-0 min-w-[140px] p-3 rounded-2xl bg-[#1c2b21]/90 backdrop-blur-md border border-white/5 shadow-lg flex flex-col gap-1">
									<div className="flex justify-between items-start">
										<span className="text-xs text-gray-400 font-medium uppercase tracking-wider">Compliance</span>
									</div>
									<div className="flex items-end gap-2 mt-1">
										<span className="text-2xl font-bold text-white leading-none">
											{fieldData?.data.compliancePercent ? `${fieldData.data.compliancePercent}%` : '0%'}
										</span>
									</div>
								</div>
							</div>
						</div>

					</main>
					{/* <!-- Bottom Sheet Container --> */}
					<div
						ref={containerRef}
						className="absolute bottom-0 left-0 w-full z-20 flex flex-col bg-[#f6f8f6] dark:bg-slate-900 rounded-t-[2rem] shadow-[0_-8px_30px_rgba(0,0,0,0.4)] border-t border-white/5 overflow-hidden transition-all duration-300 ease-out group/sheet"
						style={{
							height: `${sheetHeight}vh`,
							maxHeight: '90vh'
						}}
					>
						{/* <!-- Header with Toggle Button --> */}
						<div className="w-full flex justify-between items-center px-5 py-3 bg-inherit z-30 sticky top-0 border-b border-gray-200 dark:border-white/5">
							<h2 className="text-lg font-bold">Activity Feed</h2>
							<button
								onClick={toggleSheet}
								className="flex items-center justify-center w-10 h-10 rounded-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
								aria-label={sheetHeight === collapsedHeight ? 'Expand' : 'Collapse'}
							>
								{sheetHeight === collapsedHeight ? (
									<ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-300" />
								) : (
									<ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-300" />
								)}
							</button>
						</div>
						{sheetHeight > 20 && (
							<>
								{sheetHeight > 35 && (
									<div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 px-5 pt-2 animate-in fade-in slide-in-from-top-2 duration-200">
										<button 
											key="ALL"
											onClick={() => setSelectedCategory('ALL')}
											className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${
												selectedCategory === 'ALL' 
													? 'bg-[#13ec5b] text-slate-900 shadow-sm shadow-[#13ec5b]/20' 
													: 'bg-white dark:bg-[#1c2b21] border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5'
											}`}
										>
											All
										</button>
										<button 
											key="CANVASSING"
											onClick={() => setSelectedCategory('CANVASSING')}
											className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
												selectedCategory === 'CANVASSING' 
													? 'bg-[#13ec5b] text-slate-900 shadow-sm shadow-[#13ec5b]/20' 
													: 'bg-white dark:bg-[#1c2b21] border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5'
											}`}
										>
											Canvassing
										</button>
										<button 
											key="ENGAGEMENTS"
											onClick={() => setSelectedCategory('ENGAGEMENTS')}
											className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
												selectedCategory === 'ENGAGEMENTS' 
													? 'bg-[#13ec5b] text-slate-900 shadow-sm shadow-[#13ec5b]/20' 
													: 'bg-white dark:bg-[#1c2b21] border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5'
											}`}
										>
											Registrations
										</button>
										<button 
											key="ALERTS"
											onClick={() => setSelectedCategory('ALERTS')}
											className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
												selectedCategory === 'ALERTS' 
													? 'bg-[#13ec5b] text-slate-900 shadow-sm shadow-[#13ec5b]/20' 
													: 'bg-white dark:bg-[#1c2b21] border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5'
											}`}
										>
											Alerts
										</button>
									</div>
								)}
								{sheetHeight > 35 && (
									<div className="overflow-y-auto overflow-x-hidden flex-1 p-4 pb-24 bg-[#f6f8f6] dark:bg-slate-900 animate-in fade-in slide-in-from-bottom-2 duration-200">
										{/* Loading state */}
										{isActivityLoading && (
											<div className="flex items-center justify-center py-8">
												<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3"></div>
											</div>
										)}
										
										{/* Activity content */}
										{!isActivityLoading && activityData?.data.content && (
											<div className="space-y-4">
												{activityData?.data?.content.map((activity) => (
													<div key={activity?.id} className="flex gap-3 items-start p-3 rounded-2xl hover:bg-white dark:hover:bg-[#1c2b21]/50 transition-colors border border-transparent hover:border-gray-200 dark:hover:border-white/5">
														<div className="relative shrink-0">
															<div className={`size-10 rounded-full ${getAvatarColor(activity?.actor?.role)}/20 flex items-center justify-center ${getAvatarColor(activity?.actor?.role)} text-white font-bold border ${getAvatarColor(activity?.actor?.role)}/30`}>
																{getInitials(activity?.actor?.fullName)}
															</div>
															<div className={`absolute -bottom-0.5 -right-0.5 size-3 ${getStatusColor(activity?.type)} rounded-full border-2 border-slate-900`}></div>
														</div>
														<div className="flex-1 min-w-0">
															<div className="flex justify-between items-baseline mb-0.5">
																<h3 className="text-sm font-bold truncate text-slate-900 dark:text-white">{activity?.actor?.fullName}</h3>
																<span className="text-xs text-gray-500 font-medium whitespace-nowrap">
																	{formatDistanceToNow(new Date(activity?.createdAt), { addSuffix: true })}
																</span>
															</div>
															<p className="text-sm text-gray-600 dark:text-gray-300 leading-snug">{activity?.message}</p>
															<div className="flex gap-2 mt-2">
																<span className="inline-flex items-center gap-1 text-[10px] font-bold text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-900/30 px-2 py-0.5 rounded-md">
																	{activity?.type.replace('_', ' ')}
																</span>
															</div>
														</div>
													</div>
												))}
											</div>
										)}
										
										{/* Empty state */}
										{!isActivityLoading && (!activityData?.data.content || activityData.data.content.length === 0) && (
											<div className="flex items-center justify-center py-8">
												<p className="text-gray-500 text-sm">No activity found for this category</p>
											</div>
										)}
									</div>
								)}
							</>
						)}
					</div>
				</div>
			</div>
		</AdminLayout>
	)
}

export default FieldActivity;
